import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type ColumnDef,
  type ColumnSizingState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import CommonTable, { type CommonTableProps } from '../common/CommonTable';
import { TABLE_STYLES } from '../../constants/tableStyles';
import { SEARCE_COLORS } from '../../theme';
import type { DataTableColumnDef, DataTableColumnMeta } from './dataTableTypes';
import { renderDataTableCell } from './renderCell';
import type { DataTableBodyRowContext } from './DataTable';
import { ColumnVisibilityMenu, formatColumnIdAsLabel } from './ColumnVisibilityMenu';
import type { TableId } from '../../constants/tableTotalsConfig';
import { computeTableTotals } from '../../utils/tableTotals';
import { TableTotalsFooterRow } from './TableTotalsFooterRow';

export type TanStackDataTableProps<TData> = Omit<
  CommonTableProps,
  'children' | 'count' | 'page' | 'rowsPerPage' | 'onPageChange' | 'onRowsPerPageChange'
> & {
  /** Full dataset; sorting and pagination are applied inside via TanStack Table. */
  data: TData[];
  dataTableColumns: DataTableColumnDef<TData>[];
  getRowId: (row: TData, index: number) => string;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  /** Column ids (DataTable `id`) that participate in sorting. */
  sortableColumnIds: string[];
  /** Ascending comparator for `(rowA, rowB, columnId)`; TanStack inverts when `desc` is true. */
  compareRows: (a: TData, b: TData, columnId: string) => number;
  onRowClick?: (row: TData) => void;
  tableSx?: SxProps<Theme>;
  disableDefaultTableCellBorderReset?: boolean;
  headerCellBaseSx?: SxProps<Theme>;
  headerRowSx?: SxProps<Theme>;
  bodyRowSx?: SxProps<Theme>;
  resolveBodyRowSx?: (ctx: DataTableBodyRowContext<TData>) => SxProps<Theme>;
  rowHover?: boolean;
  bodyCellBaseSx?: SxProps<Theme>;
  embedded?: boolean;
  rowsPerPageOptions?: number[];
  /** When true, shows a column visibility menu (TanStack `columnVisibility` state). */
  enableColumnVisibility?: boolean;
  /** Controlled column visibility; pair with `onColumnVisibilityChange` for controlled mode. */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  /** Menu labels by column id; merged with `meta.columnMenuLabel`. */
  columnMenuLabels?: Record<string, string>;
  /** Label on the column visibility trigger. Default: `Show/Hide Columns`. */
  columnVisibilityTriggerLabel?: string;
  /**
   * When true with `enableColumnVisibility`, the built-in column menu is not rendered in CommonTable's header row.
   * Use controlled `columnVisibility` / `onColumnVisibilityChange` and render `ColumnVisibilityMenu` in the parent
   * (avoids an extra row above the table that can clash with sticky blue headers).
   */
  detachColumnVisibilityMenu?: boolean;
  /**
   * Suppress the built-in column-visibility menu entirely (both header and detached toolbar)
   * while still applying `columnVisibility`. Use when the parent renders its own column menu.
   */
  hideColumnVisibilityMenu?: boolean;
  /**
   * When `detachColumnVisibilityMenu` is true: optional content before the column menu on the same toolbar row
   * (e.g. “Clear all filters”). Rendered only when this row is shown.
   */
  detachedToolbarLeading?: React.ReactNode;
  /**
   * When `detachColumnVisibilityMenu` is true: layout of the detached toolbar row.
   * `space-between` — leading content left, column menu right (e.g. period strip + Filter columns).
   */
  detachedToolbarJustify?: 'flex-end' | 'space-between';
  /** When `pagedRows` is empty, render this inside `<TableBody>` (e.g. one `<TableRow>`). Receives visible column count for `colSpan`. */
  renderEmptyBody?: (visibleColumnCount: number) => React.ReactNode;
  /** Optional extra row(s) after each data row (e.g. expandable detail). Use `TableRow` + `TableCell colSpan={visibleColumnCount}`. */
  renderAfterRow?: (ctx: { row: TData; rowIndex: number; visibleColumnCount: number }) => React.ReactNode;
  /**
   * Sheet-style column resize (drag handle + visible separator on header). Uses TanStack `columnSizing` with
   * `table-layout: fixed` and explicit column widths. **Default: true** for all `TanStackDataTable` instances;
   * pass `false` to disable (e.g. very constrained layouts).
   */
  enableColumnResizing?: boolean;
  /**
   * When set, reads `src/constants/tableTotalsConfig.ts` and renders a footer row with column totals.
   * Totals use the full `data` array (filtered dataset), not only the current page.
   */
  tableId?: TableId;
  /**
   * When set, replaces the default single-row `<TableHead>` (e.g. grouped month sub-columns).
   * Body cells must still align with `columnsToRender` order.
   */
  customTableHead?: React.ReactNode;
};

/** First *visible* column (index 0 after visibility filter) — left align even when `meta.align` is center. */
const firstVisibleColumnHeaderShellSx: SxProps<Theme> = {
  textAlign: 'left',
  '& > *': { textAlign: 'left' },
  /* Utilization-style headers: outer Box + inner inline-flex for label + filter */
  '& > * > *': { justifyContent: 'flex-start' },
};

const firstVisibleColumnBodyShellSx: SxProps<Theme> = {
  textAlign: 'left',
  '& > a.MuiLink-root': { justifyContent: 'flex-start' },
  '& > .MuiBox-root': { justifyContent: 'flex-start' },
};

function readMetaPx(meta: DataTableColumnMeta | undefined, key: 'minWidth' | 'width'): number | undefined {
  const v = meta?.[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+(\.\d+)?px$/.test(v)) return parseFloat(v);
  return undefined;
}

/** Initial TanStack `size` from DataTable `meta` (falls back for columns without pixel hints). */
function initialTanStackColumnSizeFromMeta(meta: DataTableColumnMeta | undefined): number {
  return readMetaPx(meta, 'width') ?? readMetaPx(meta, 'minWidth') ?? 150;
}

const defaultHeaderCellBaseSx: SxProps<Theme> = {
  ...TABLE_STYLES.cellHeader,
  borderBottom: `1px solid ${SEARCE_COLORS.border}`,
  backgroundColor: SEARCE_COLORS.neutral_100,
  color: SEARCE_COLORS.textPrimary,
  fontWeight: 600,
};

/**
 * CommonTable + MUI Table + DataTable-style column defs, with TanStack Table for sort + pagination.
 */
function TanStackDataTable<TData>({
  data,
  dataTableColumns,
  getRowId,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  sortableColumnIds,
  compareRows,
  onRowClick,
  tableSx,
  disableDefaultTableCellBorderReset = false,
  headerCellBaseSx = defaultHeaderCellBaseSx,
  headerRowSx,
  bodyRowSx,
  resolveBodyRowSx,
  rowHover = false,
  bodyCellBaseSx,
  embedded = false,
  rowsPerPageOptions = [5, 10, 25, 50],
  enableColumnVisibility = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange: controlledOnColumnVisibilityChange,
  columnMenuLabels,
  columnVisibilityTriggerLabel,
  detachColumnVisibilityMenu = false,
  hideColumnVisibilityMenu = false,
  detachedToolbarLeading,
  detachedToolbarJustify = 'flex-end',
  renderEmptyBody,
  renderAfterRow,
  enableColumnResizing = true,
  tableId,
  customTableHead,
  ...commonTableProps
}: TanStackDataTableProps<TData>): React.ReactElement {
  const { headerAction: userHeaderAction, ...commonTableRest } = commonTableProps;

  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
  const columnVisibility =
    controlledColumnVisibility !== undefined ? controlledColumnVisibility : internalColumnVisibility;
  const onColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
    (updater) => {
      if (controlledOnColumnVisibilityChange) {
        controlledOnColumnVisibilityChange(updater);
      } else {
        setInternalColumnVisibility(updater);
      }
    },
    [controlledOnColumnVisibilityChange]
  );

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const sortableSet = useMemo(() => new Set(sortableColumnIds), [sortableColumnIds]);

  const visibleLeafColumnCount = useMemo(() => {
    if (!enableColumnVisibility) return dataTableColumns.length;
    return dataTableColumns.filter((col) => columnVisibility[col.id] !== false).length;
  }, [dataTableColumns, columnVisibility, enableColumnVisibility]);

  const sizingEnabled = Boolean(enableColumnResizing && visibleLeafColumnCount > 1);

  const tanStackColumns = useMemo<ColumnDef<TData, unknown>[]>(
    () =>
      dataTableColumns.map((col) => ({
        id: col.id,
        accessorFn: () => null,
        enableSorting: sortableSet.has(col.id),
        enableHiding: enableColumnVisibility && col.meta?.hideable !== false,
        sortingFn: (rowA, rowB, columnId) => compareRows(rowA.original, rowB.original, columnId),
        ...(enableColumnResizing
          ? {
              size: initialTanStackColumnSizeFromMeta(col.meta),
              minSize: 72,
              maxSize: 960,
            }
          : {}),
      })),
    [dataTableColumns, sortableSet, compareRows, enableColumnVisibility, enableColumnResizing]
  );

  const table = useReactTable({
    data,
    columns: tanStackColumns,
    state: {
      sorting,
      pagination,
      ...(enableColumnVisibility ? { columnVisibility } : {}),
      ...(sizingEnabled ? { columnSizing } : {}),
    },
    onSortingChange,
    onPaginationChange,
    ...(enableColumnVisibility ? { onColumnVisibilityChange } : {}),
    ...(sizingEnabled
      ? {
          enableColumnResizing: true,
          columnResizeMode: 'onChange' as const,
          onColumnSizingChange: setColumnSizing,
        }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row, index) => getRowId(row as TData, index),
  });

  const pagedRows = table.getRowModel().rows.map((r) => r.original);

  const visibleDataTableColumns = useMemo(() => {
    if (!enableColumnVisibility) return dataTableColumns;
    return dataTableColumns.filter((col) => columnVisibility[col.id] !== false);
  }, [dataTableColumns, columnVisibility, enableColumnVisibility]);

  /** One visible column should span the table (avoids broken `calc(100% - …)` layouts). */
  const columnsToRender = useMemo(() => {
    if (visibleDataTableColumns.length !== 1) return visibleDataTableColumns;
    const c = visibleDataTableColumns[0];
    const m = c.meta ?? {};
    return [
      {
        ...c,
        meta: {
          ...m,
          width: '100%',
          maxWidth: '100%',
        },
      },
    ];
  }, [visibleDataTableColumns]);

  const footerTotals = useMemo(() => {
    if (!tableId || data.length === 0) return null;
    return computeTableTotals(
      tableId,
      data as Record<string, unknown>[],
      columnsToRender.map((c) => c.id),
    );
  }, [tableId, data, columnsToRender]);

  const columnVisibilityMenuItems = useMemo(() => {
    if (!enableColumnVisibility) return [];
    return dataTableColumns
      .filter((col) => col.meta?.hideable !== false)
      .map((col) => ({
        id: col.id,
        label: columnMenuLabels?.[col.id] ?? col.meta?.columnMenuLabel ?? formatColumnIdAsLabel(col.id),
        hideable: true as const,
      }));
  }, [dataTableColumns, enableColumnVisibility, columnMenuLabels]);

  const mergedHeaderAction =
    enableColumnVisibility &&
    columnVisibilityMenuItems.length > 0 &&
    !detachColumnVisibilityMenu &&
    !hideColumnVisibilityMenu ? (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minWidth: 0,
          justifyContent: 'flex-end',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'row',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: 1.5,
            maxWidth: '100%',
          }}
        >
          {userHeaderAction}
          <ColumnVisibilityMenu
            items={columnVisibilityMenuItems}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            triggerLabel={columnVisibilityTriggerLabel}
          />
        </Box>
      </Box>
    ) : (
      userHeaderAction
    );

  const showDetachedFilterToolbar =
    detachColumnVisibilityMenu &&
    enableColumnVisibility &&
    !hideColumnVisibilityMenu &&
    (columnVisibilityMenuItems.length > 0 || detachedToolbarLeading != null);

  useEffect(() => {
    const total = data.length;
    const pageSize = pagination.pageSize;
    const maxPage = total === 0 ? 0 : Math.max(0, Math.ceil(total / pageSize) - 1);
    if (pagination.pageIndex > maxPage) {
      onPaginationChange((prev) => ({ ...prev, pageIndex: maxPage }));
    }
  }, [data.length, pagination.pageIndex, pagination.pageSize, onPaginationChange]);

  const headerBase = headerCellBaseSx;
  const defaultBodyCellStack: SxProps<Theme>[] = [TABLE_STYLES.cell as any, { py: 0.75 }];
  const bodyCellStack: SxProps<Theme>[] =
    bodyCellBaseSx != null ? [bodyCellBaseSx as any] : defaultBodyCellStack;

  return (
    <>
      {showDetachedFilterToolbar && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            ...TABLE_STYLES.tableToolbarTop,
            pb: 2,
            minHeight: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: detachedToolbarJustify === 'space-between' ? 'space-between' : 'flex-end',
            flexWrap: 'nowrap',
            gap: 1.25,
            borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
            backgroundColor: SEARCE_COLORS.white,
            position: 'relative',
            zIndex: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {detachedToolbarJustify === 'space-between' ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  gap: 0.75,
                  minWidth: 0,
                  flex: '1 1 auto',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                }}
              >
                {detachedToolbarLeading}
              </Box>
              {columnVisibilityMenuItems.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <ColumnVisibilityMenu
                    items={columnVisibilityMenuItems}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    triggerLabel={columnVisibilityTriggerLabel}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                alignItems: 'center',
                gap: 1.5,
                maxWidth: '100%',
              }}
            >
              {detachedToolbarLeading}
              {columnVisibilityMenuItems.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <ColumnVisibilityMenu
                    items={columnVisibilityMenuItems}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    triggerLabel={columnVisibilityTriggerLabel}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      <CommonTable
      {...commonTableRest}
      headerAction={mergedHeaderAction}
      count={data.length}
      page={pagination.pageIndex}
      rowsPerPage={pagination.pageSize}
      onPageChange={(_, newPage) => {
        onPaginationChange((prev) => ({ ...prev, pageIndex: newPage }));
      }}
      onRowsPerPageChange={(e) => {
        const next = parseInt(e.target.value, 10);
        onPaginationChange({ pageIndex: 0, pageSize: next });
      }}
      rowsPerPageOptions={rowsPerPageOptions}
    >
      <Table
        stickyHeader
        size={TABLE_STYLES.tableSize}
        sx={
          [
            disableDefaultTableCellBorderReset
              ? { width: '100%' }
              : {
                  width: '100%',
                  '& .MuiTableCell-root': {
                    borderRight: 'none',
                    borderLeft: 'none',
                  },
                },
            {
              '& thead .MuiTableRow-root': { display: 'table-row' },
              '& thead .MuiTableCell-root': {
                display: 'table-cell',
                ...(embedded ? { borderRadius: 0 } : {}),
              },
            },
            ...(tableSx != null ? [tableSx as any] : []),
            sizingEnabled
              ? {
                  tableLayout: 'fixed' as const,
                  width: `${table.getTotalSize()}px`,
                  minWidth: '100%',
                }
              : {},
          ] as SxProps<Theme>
        }
      >
        <TableHead>
          {customTableHead ?? (
          <TableRow sx={headerRowSx}>
            {columnsToRender.map((col, colIndex) => {
              const m = col.meta;
              const isFirstVisible = colIndex === 0;
              const useFirstColumnShell = isFirstVisible && !m?.preserveMetaAlignWhenFirstVisible;
              const headerContent =
                typeof col.header === 'function' ? col.header({ column: col }) : col.header;
              const hp = m?.headerCellProps;
              const { sx: headerPropsSx, ...headerRest } = hp ?? {};
              const header = table.getHeaderGroups()[0]?.headers.find((h) => h.column.id === col.id);
              const sizePx = sizingEnabled ? table.getColumn(col.id)?.getSize() : undefined;
              const useTanStackWidth = sizingEnabled && sizePx != null;
              return (
                <TableCell
                  key={col.id}
                  align={useFirstColumnShell ? 'left' : m?.align}
                  colSpan={m?.colSpan}
                  {...headerRest}
                  sx={[
                    headerBase as any,
                    useTanStackWidth
                      ? {
                          width: sizePx,
                          minWidth: sizePx,
                          maxWidth: sizePx,
                          boxSizing: 'border-box',
                          position: 'relative',
                        }
                      : {},
                    !useTanStackWidth && m?.width != null
                      ? { width: m.width, minWidth: m.minWidth ?? m.width, maxWidth: m.maxWidth ?? m.width }
                      : {},
                    !useTanStackWidth && m?.minWidth != null && m?.width == null ? { minWidth: m.minWidth } : {},
                    !useTanStackWidth && m?.maxWidth != null && m?.width == null ? { maxWidth: m.maxWidth } : {},
                    (m?.headerCellSx as any) ?? {},
                    ...(headerPropsSx != null ? [headerPropsSx as any] : []),
                    ...(useFirstColumnShell ? [firstVisibleColumnHeaderShellSx as any] : []),
                  ]}
                >
                  <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
                    {headerContent}
                  </Box>
                  {useTanStackWidth && header?.column.getCanResize() ? (
                    <Box
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.id}`}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        // Wider invisible hit-area so the thin visual line is still easy to grab.
                        width: 8,
                        cursor: 'col-resize',
                        userSelect: 'none',
                        touchAction: 'none',
                        zIndex: 2,
                        backgroundColor: 'transparent',
                        /** Thin vertical separator flush with the cell's right edge — reads like a column border. */
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          right: 0,
                          width: '1px',
                          backgroundColor: header.column.getIsResizing()
                            ? '#fff'
                            : 'rgba(255,255,255,0.35)',
                          pointerEvents: 'none',
                          transition: 'background-color 120ms ease',
                        },
                        '&:hover::after': {
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  ) : null}
                </TableCell>
              );
            })}
          </TableRow>
          )}
        </TableHead>
        <TableBody>
          {pagedRows.length === 0 && renderEmptyBody != null ? (
            renderEmptyBody(columnsToRender.length)
          ) : (
            pagedRows.map((row, rowIndex) => {
              const resolvedBodyRowSx = resolveBodyRowSx?.({ row, rowIndex });
              const rowKey = getRowId(row, rowIndex);
              const after = renderAfterRow?.({
                row,
                rowIndex,
                visibleColumnCount: columnsToRender.length,
              });
              return (
                <Fragment key={rowKey}>
                  <TableRow
                    hover={Boolean(onRowClick) || rowHover}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={[
                      TABLE_STYLES.tableRowHover as any,
                      onRowClick ? { cursor: 'pointer' } : {},
                      { '& .MuiTableCell-root': { verticalAlign: 'middle' } },
                      ...(bodyRowSx != null ? [bodyRowSx as any] : []),
                      ...(resolvedBodyRowSx != null ? [resolvedBodyRowSx as any] : []),
                    ]}
                  >
                    {columnsToRender.map((col, colIndex) => {
                      const m = col.meta;
                      const isFirstVisible = colIndex === 0;
                      const useFirstColumnShell = isFirstVisible && !m?.preserveMetaAlignWhenFirstVisible;
                      const sizePx = sizingEnabled ? table.getColumn(col.id)?.getSize() : undefined;
                      const useTanStackWidth = sizingEnabled && sizePx != null;
                      return (
                        <TableCell
                          key={col.id}
                          align={useFirstColumnShell ? 'left' : m?.align}
                          colSpan={m?.colSpan}
                          sx={[
                            ...bodyCellStack.map((s) => s as any),
                            useTanStackWidth
                              ? {
                                  width: sizePx,
                                  minWidth: sizePx,
                                  maxWidth: sizePx,
                                  boxSizing: 'border-box',
                                  paddingRight: '18px',
                                }
                              : {},
                            !useTanStackWidth && m?.width != null
                              ? { width: m.width, minWidth: m.minWidth ?? m.width, maxWidth: m.maxWidth ?? m.width }
                              : {},
                            !useTanStackWidth && m?.minWidth != null && m?.width == null ? { minWidth: m.minWidth } : {},
                            !useTanStackWidth && m?.maxWidth != null && m?.width == null ? { maxWidth: m.maxWidth } : {},
                            (m?.cellSx as any) ?? {},
                            ...(useFirstColumnShell ? [firstVisibleColumnBodyShellSx as any] : []),
                          ]}
                        >
                          {renderDataTableCell(col, { row, rowIndex })}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {after}
                </Fragment>
              );
            })
          )}
          {footerTotals != null && (
            <TableTotalsFooterRow
              columns={columnsToRender}
              totals={footerTotals}
              resolveCellSx={(col) => {
                const sizePx = sizingEnabled ? table.getColumn(col.id)?.getSize() : undefined;
                if (!sizingEnabled || sizePx == null) return {};
                return {
                  width: sizePx,
                  minWidth: sizePx,
                  maxWidth: sizePx,
                  boxSizing: 'border-box',
                };
              }}
            />
          )}
        </TableBody>
      </Table>
    </CommonTable>
    </>
  );
}

export default TanStackDataTable;
